import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useTheme } from '../context/ThemeContext';
import { getProductBySlug as getDefaultProduct } from '../data/productCategories';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductCategory = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mapping from slug to translation key
  const productTranslationKeys = {
    'led-signs': { name: 'products.ledSigns', desc: 'products.ledSignsDesc' },
    'led-neon-signs': { name: 'products.neonSigns', desc: 'products.neonSignsDesc' },
    'channel-letter-signs': { name: 'products.channelLetters', desc: 'products.channelLettersDesc' },
    'acrylic-signs': { name: 'products.acrylicSigns', desc: 'products.acrylicSignsDesc' },
    'metal-signs': { name: 'products.metalSigns', desc: 'products.metalSignsDesc' },
    'lightbox-signs': { name: 'products.lightboxSigns', desc: 'products.lightboxSignsDesc' },
    'custom-signs': { name: 'products.customSigns', desc: 'products.customSignsDesc' }
  };

  const getProductName = () => {
    const key = productTranslationKeys[slug];
    return key ? t(key.name) : product?.name;
  };

  const getProductDesc = () => {
    const key = productTranslationKeys[slug];
    return key ? t(key.desc) : product?.description;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // Try to fetch from API first
        const response = await fetch(`${API_URL}/api/content/products`);
        if (response.ok) {
          const products = await response.json();
          const apiProduct = products.find(p => p.slug === slug);
          if (apiProduct && apiProduct.gallery && apiProduct.gallery.length > 0) {
            setProduct(apiProduct);
            setSelectedImage(apiProduct.gallery[0]);
            window.scrollTo(0, 0);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
      
      // Fallback to hardcoded data
      const productData = getDefaultProduct(slug);
      setProduct(productData);
      if (productData?.gallery?.length > 0) {
        setSelectedImage(productData.gallery[0]);
      }
      window.scrollTo(0, 0);
    };
    
    fetchProduct();
  }, [slug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: t('productCategory.quoteRequestSent'),
      description: t('productCategory.quoteRequestDesc'),
    });
    
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    setIsSubmitting(false);
  };

  if (!product) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center pt-20"
        style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
      >
        <div className="text-center">
          <h1 
            className="text-3xl font-bold mb-4"
            style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
          >
            {t('productCategory.notFound')}
          </h1>
          <Link 
            to="/"
            className="text-lg underline"
            style={{ color: isDark ? '#00ffff' : '#0f172a' }}
          >
            {t('common.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-24 md:pt-28 pb-16 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 mb-8 font-medium transition-all duration-300 hover:scale-105"
          style={{
            color: isDark ? '#00ffff' : '#0f172a',
            textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
          }}
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.backToHome')}
        </Link>

        {/* Product Header */}
        <div className="mb-12">
          <h1 
            className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'gradient-text-dark' : 'gradient-text-light'}`}
            style={{
              filter: isDark 
                ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.5))'
                : 'drop-shadow(0 0 10px rgba(255, 20, 147, 0.4))'
            }}
            data-testid="product-title"
          >
            {getProductName()}
          </h1>
          <p 
            className="text-lg md:text-xl max-w-3xl"
            style={{ color: isDark ? '#e0e0e0' : '#475569' }}
          >
            {getProductDesc()}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Gallery */}
          <div>
            {/* Main Image */}
            <div 
              className="relative rounded-2xl overflow-hidden mb-4 aspect-video"
              style={{
                border: `3px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
                boxShadow: isDark 
                  ? '0 0 40px rgba(255, 0, 255, 0.3)' 
                  : '0 0 30px rgba(255, 20, 147, 0.2)'
              }}
            >
              <img 
                src={selectedImage?.image} 
                alt={selectedImage?.title}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              <div 
                className="absolute bottom-0 left-0 right-0 p-4"
                style={{
                  background: isDark 
                    ? 'linear-gradient(transparent, rgba(0, 0, 0, 0.9))'
                    : 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))'
                }}
              >
                <span className="text-white font-medium">{selectedImage?.title}</span>
              </div>
            </div>

            {/* Thumbnail Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {product.gallery.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedImage(item)}
                  className={`relative rounded-lg overflow-hidden aspect-square transition-all duration-300 ${
                    selectedImage?.id === item.id ? 'ring-2 scale-105' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    ringColor: isDark ? '#00ffff' : '#00bfff'
                  }}
                  data-testid={`gallery-thumb-${item.id}`}
                >
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Features List */}
            <div 
              className="mt-8 p-6 rounded-2xl"
              style={{
                backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(249, 250, 251, 1)',
                border: `2px solid ${isDark ? '#00ffff' : '#00bfff'}`,
                boxShadow: isDark 
                  ? '0 0 30px rgba(0, 255, 255, 0.2)' 
                  : '0 0 20px rgba(0, 191, 255, 0.15)'
              }}
            >
              <h3 
                className="text-xl font-bold mb-4"
                style={{
                  color: isDark ? '#00ffff' : '#00bfff',
                  textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.6)' : 'none'
                }}
              >
                {t('productCategory.features')}
              </h3>
              <ul className="space-y-3">
                {product.features.map((feature, index) => (
                  <li 
                    key={index}
                    className="flex items-start gap-3"
                    style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                  >
                    <Check 
                      className="w-5 h-5 mt-0.5 flex-shrink-0" 
                      style={{ color: isDark ? '#00ff88' : '#10b981' }}
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Link to Customizer for LED Neon */}
            {product.hasCustomizer && (
              <Link
                to="/#customizer"
                className="mt-6 block text-center px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)'
                    : 'linear-gradient(135deg, #ff1493 0%, #00bfff 100%)',
                  color: '#ffffff',
                  boxShadow: isDark 
                    ? '0 10px 40px rgba(255, 0, 255, 0.4)' 
                    : '0 10px 30px rgba(255, 20, 147, 0.3)'
                }}
                data-testid="customizer-link"
              >
                {t('productCategory.tryCustomizer')} →
              </Link>
            )}
          </div>

          {/* Right: Quote Form */}
          <div 
            className="p-8 rounded-2xl h-fit"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 1)',
              border: `3px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
              boxShadow: isDark 
                ? '0 0 50px rgba(255, 0, 255, 0.3)' 
                : '0 0 40px rgba(255, 20, 147, 0.2)'
            }}
          >
            <h2 
              className="text-2xl font-bold mb-2"
              style={{
                color: isDark ? '#ff00ff' : '#ff1493',
                textShadow: isDark ? '0 0 15px rgba(255, 0, 255, 0.6)' : 'none'
              }}
            >
              {t('productCategory.requestQuote')}
            </h2>
            <p 
              className="mb-6"
              style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
            >
              {t('productCategory.tellUsAbout')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                  >
                    {t('quoteForm.name')} *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t('quoteForm.namePlaceholder')}
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a'
                    }}
                    data-testid="quote-name-input"
                  />
                </div>
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                  >
                    {t('quoteForm.email')} *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t('quoteForm.emailPlaceholder')}
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a'
                    }}
                    data-testid="quote-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                  >
                    {t('quoteForm.phone')}
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('quoteForm.phonePlaceholder')}
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a'
                    }}
                    data-testid="quote-phone-input"
                  />
                </div>
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                  >
                    {t('productCategory.company')}
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder={t('productCategory.companyPlaceholder')}
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a'
                    }}
                    data-testid="quote-company-input"
                  />
                </div>
              </div>

              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                >
                  {t('productCategory.projectDetails')} *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder={t('productCategory.projectPlaceholder')}
                  className="transition-all duration-300 resize-none"
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                  data-testid="quote-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 text-lg font-bold transition-all duration-300 hover:scale-105 disabled:opacity-50"
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                    : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                  color: '#ffffff',
                  boxShadow: isDark 
                    ? '0 10px 40px rgba(255, 0, 255, 0.5)' 
                    : '0 10px 30px rgba(255, 20, 147, 0.4)'
                }}
                data-testid="quote-submit-button"
              >
                {isSubmitting ? (
                  t('quoteForm.sending')
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('productCategory.sendQuoteRequest')}
                  </>
                )}
              </Button>
            </form>

            <p 
              className="text-center text-sm mt-4"
              style={{ color: isDark ? '#707070' : '#94a3b8' }}
            >
              We typically respond within 24 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCategory;
